import { STATIONS, STORY } from "@/content/landing";
import { formatStay, stopMinutes } from "@/lib/stay-clock";
import { stayLabel } from "./StayStrip";

const FIRST = formatStay(stopMinutes(STATIONS[0]));

const cropStyle = (crop?: { scale: number; origin: string }) =>
  crop ? { scale: String(crop.scale), transformOrigin: crop.origin } : undefined;

/**
 * 체류 시계 — 스토리 구간의 현재 시각. 정거장 글에 같은 시각이 이미 적혀 있어 보조기기에는
 * 숨긴다(스크롤할 때마다 바뀌는 숫자를 읽어 주면 소음이다). 값은 landing-motion 이 바꾼다.
 */
function StayClock({ compact = false }: { compact?: boolean }) {
  return (
    <div aria-hidden="true" className={compact ? "flex items-baseline gap-3" : ""}>
      <p className="sl-time flex items-baseline gap-2 text-[13px] font-semibold">
        <span data-clock-day>{FIRST.day}</span>
        <span className="sl-muted font-medium" data-clock-label>
          {STATIONS[0].short}
        </span>
      </p>
      <p
        className={
          compact
            ? "sl-time ml-auto text-2xl font-extrabold leading-none tracking-[-0.04em]"
            : "sl-time mt-2 text-[clamp(4.5rem,1rem+6.2vw,8.75rem)] font-extrabold leading-[0.86] tracking-[-0.06em]"
        }
      >
        <span data-clock-hh>{FIRST.hh}</span>
        <span className="sl-colon">:</span>
        <span data-clock-mm>{FIRST.mm}</span>
      </p>
    </div>
  );
}

/**
 * 시그니처 구간. 왼쪽은 체류의 선(스크롤만큼 그려진다)과 정거장, 오른쪽은 고정된 무대 —
 * 체류 시계와 그 시각의 실제 게스트 화면. 밤 정거장에 들어서면 구간 전체가 밤이 된다.
 * 모바일은 무대 대신 각 정거장 아래에 화면이 붙고, 시계는 구간 위쪽에 얇게 고정된다.
 */
export function StayStory() {
  return (
    <section data-stay data-tone="day" aria-labelledby="stay-title" className="sl-story relative">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 pb-16 pt-24 md:pb-24 md:pt-40">
        <header className="grid md:grid-cols-12 md:gap-x-8">
          <div className="md:col-span-7 md:col-start-2">
            <h2 id="stay-title" className="sl-xl" data-split>
              {STORY.title}
            </h2>
            <p className="sl-body sl-muted mt-6 max-w-[34rem]">{STORY.lede}</p>
          </div>
        </header>

        <div className="sticky top-16 z-10 -mx-4 mt-12 bg-[var(--sl-ground)] px-4 sm:-mx-6 sm:px-6 py-3 transition-colors duration-700 md:hidden">
          <StayClock compact />
          <div className="sl-rule absolute inset-x-0 bottom-0" />
        </div>

        <div className="relative mt-6 grid md:mt-20 md:grid-cols-12 md:gap-x-8">
          <ol data-stay-list className="sl-stay-list relative md:col-span-7">
            <li aria-hidden="true" className="sl-spine">
              <span className="sl-spine__track" />
              <span className="sl-spine__fill" data-stay-fill style={{ transform: "scaleY(0)" }} />
              <span className="sl-spine__head" data-stay-head />
            </li>
            {STATIONS.map((s, i) => (
              <li
                key={s.id}
                id={`stay-${s.id}`}
                data-stay-station
                data-state={i === 0 ? "active" : "future"}
                className="relative min-h-[78svh] scroll-mt-24 py-14 pl-10 md:min-h-[88svh] md:py-[12svh] md:pl-[5.5rem]"
              >
                {/* 데스크톱: 글이 화면 가운데쯤에 붙어 오른쪽 시계·화면과 늘 나란히 선다. */}
                <article className="sl-stop__text relative max-w-[32rem] md:sticky md:top-[38svh]">
                  <span aria-hidden="true" className="sl-stop__mark" />
                  <p className="sl-stop__time sl-time text-[13px] font-semibold">{stayLabel(i)}</p>
                  <h3 className="sl-l mt-3">{s.title}</h3>
                  <p className="sl-body sl-muted mt-4">{s.body}</p>
                  <div className="sl-phone mt-8 w-[76%] max-w-[300px] md:hidden">
                    <div className="sl-phone__glass">
                    <img
                      src={s.screen.src}
                      alt={s.screen.alt}
                      width={402}
                      height={874}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                      style={cropStyle(s.screen.crop)}
                    />
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ol>

          <div className="hidden md:col-span-5 md:col-start-8 md:block">
            <div className="sticky top-[calc(4rem+4svh)] flex h-[calc(100svh-4rem-8svh)] flex-col justify-center gap-8">
              <StayClock />
              <div className="sl-phone h-[min(58svh,600px)] self-start">
                {STATIONS.map((s, i) => (
                  // 화면마다 둥근 유리(겉 틀 안쪽)에 갇힌다 — 확대한 캡처가 틀 밖으로 번지지 않게.
                  <div
                    key={s.id}
                    data-stay-screen
                    data-active={i === 0 ? "true" : "false"}
                    className="sl-phone__screen"
                  >
                    <img
                      src={s.screen.src}
                      alt={s.screen.alt}
                      width={402}
                      height={874}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover"
                      style={cropStyle(s.screen.crop)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
